<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">{{ $t("item.bidList") }}</p>
      <button
        type="button"
        class="delete"
        @click="$emit('close')"
        aria-label="close"
      ></button>
    </header>
    <section class="modal-card-body">
      <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

      <b-notification
        v-if="errorMessage"
        type="is-danger"
        has-icon
        :closable="false"
        class="mb-4"
      >
        {{ errorMessage }}
      </b-notification>

      <b-table
        :data="bids"
        :loading="isLoading"
        :empty="$t('item.noBidsYet')"
        :narrowed="true"
        hoverable
      >
        <b-table-column
          field="bidder"
          :label="$t('item.bidListTable.bidder')"
          v-slot="props"
        >
          Budgiver {{ props.row.bidder }}
        </b-table-column>

        <b-table-column
          field="amount"
          :label="$t('item.bidListTable.amount')"
          numeric
          v-slot="props"
        >
          {{ formatNumber(props.row.amount) }},-
        </b-table-column>

        <b-table-column
          field="createdAt"
          :label="$t('item.bidListTable.date')"
          v-slot="props"
        >
          {{ formatDateTime(props.row.createdAt) }}
        </b-table-column>
      </b-table>
    </section>
    <footer class="modal-card-foot">
      <button class="button" @click="$emit('close')">
        {{ $t("general.close") }}
      </button>
    </footer>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  defineProps,
  defineEmits,
  getCurrentInstance
} from "vue";
import moment from "moment";

// Props
const props = defineProps({
  itemId: {
    type: String,
    required: true
  }
});

// Emits
const emit = defineEmits(["close"]);

// State
const bids = ref([]);
const isLoading = ref(false);
const errorMessage = ref("");

// Get current instance
const app = getCurrentInstance().proxy;

// Methods
const fetchBids = async () => {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    const response = await app.$axios.get(
      `${window.apiURL}/item/${props.itemId}/bids`
    );
    bids.value = response.data.bids.sort((a, b) => {
      // Sorter etter dato, nyeste først
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  } catch (error) {
    // Håndterer feil ved henting av budliste
    errorMessage.value =
      error.response?.data?.message || "Feil ved henting av budliste";
  } finally {
    isLoading.value = false;
  }
};

const formatNumber = value => {
  return new Intl.NumberFormat("nb-NO").format(value);
};

// Enkel formatering av dato direkte i komponenten
const formatDateTime = dateTime => {
  if (!dateTime) return "";

  // Konverter til Date-objekt hvis det er en string
  const date = typeof dateTime === "string" ? new Date(dateTime) : dateTime;

  // Sjekk om datoen er gyldig
  if (isNaN(date.getTime())) return "";

  // Formater dato med moment hvis tilgjengelig
  if (window.moment) {
    return window.moment(date).format("DD.MM.YYYY HH:mm");
  }

  // Fallback til enkel formatering
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

// Lifecycle hooks
onMounted(() => {
  fetchBids();
});
</script>
